
import {
  computed,
  ComputedRef,
  defineComponent,
  PropType,
  Ref,
  ref,
  watch,
} from 'vue';
import { StatsBuilderColumnLayoutEnum, StatsBuilderRowLayoutEnum, StatsType } from '@/types';
import { StatsBuilderColumnConfig, StatsBuilderRowConfig } from '@/types/stats-editor-types';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';
import { uniqueIdS6Generator } from '@/store/modules/stats-editor';
import Dialog from 'primevue/dialog';
import SelectButton from 'primevue/selectbutton';
import Button from 'primevue/button';

type ModalMode = 'add' | 'edit';

type ColumnOption = {
  label: string;
  value: number;
  disabled?: boolean;
};

export default defineComponent({
  name: 'StatsBuilderRowLayoutModal',

  components: {
    Dialog,
    SelectButton,
    Button,
  },

  props: {
    mode: {
      type: String as PropType<ModalMode>,
      default: 'add',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    rowData: {
      type: Object as PropType<StatsBuilderRowConfig | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'on-hide-modal'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const pageTypeState = computed(() => store.getters['statsEditor/getPageType']);

    const isEditMode = computed(() => props.mode === 'edit');

    const dialogVisible = computed({
      get: () => props.visible,
      set: (value: boolean) => {
        emit('update:visible', value);
        if (!value) {
          emit('on-hide-modal');
        }
      },
    });

    const availableColumns = computed<ColumnOption[]>(() => {
      const baseMaxColumns = pageTypeState.value === StatsType.EMAIL ? 2 : 4;

      if (isEditMode.value) {
        const currentCount = props.rowData?.children?.length ?? 1;
        const maxColumns = Math.max(baseMaxColumns, currentCount);
        return Array.from({ length: maxColumns }, (_, index) => {
          const value = index + 1;
          return {
            label: `${value}`,
            value,
            disabled: value > baseMaxColumns && value !== currentCount,
          };
        });
      }

      return Array.from({ length: baseMaxColumns }, (_, index) => ({
        label: `${index + 1}`,
        value: index + 1,
      }));
    });

    const chosenColumns: Ref<number> = ref(1);
    const layoutIndex: Ref<number> = ref(0);
    const isInitializing = ref(false);

    const availableColumnsLayouts: any[][] = [
      [
        {
          type: StatsBuilderRowLayoutEnum.TWELVE, name: t('statsEditor.addRowWidget.fullWidth'), percentages: [100], grid: ['csd-four'],
        },
      ],
      [
        {
          type: StatsBuilderRowLayoutEnum.SIX_SIX, name: '50% / 50%', percentages: [50, 50], grid: ['csd-two', 'csd-two'],
        },
        {
          type: StatsBuilderRowLayoutEnum.NINE_THREE, name: '75% / 25%', percentages: [75, 25], grid: ['csd-three', 'csd-one'],
        },
        {
          type: StatsBuilderRowLayoutEnum.THREE_NINE, name: '25% / 75%', percentages: [25, 75], grid: ['csd-one', 'csd-three'],
        },
      ],
      [
        {
          type: StatsBuilderRowLayoutEnum.SIX_THREE_THREE, name: '50% / 25% / 25%', percentages: [50, 25, 25], grid: ['csd-two', 'csd-one', 'csd-one'],
        },
        {
          type: StatsBuilderRowLayoutEnum.THREE_SIX_THREE, name: '25% / 50% / 25%', percentages: [25, 50, 25], grid: ['csd-one', 'csd-two', 'csd-one'],
        },
        {
          type: StatsBuilderRowLayoutEnum.THREE_THREE_SIX, name: '25% / 25% / 50%', percentages: [25, 25, 50], grid: ['csd-one', 'csd-one', 'csd-two'],
        },
        {
          type: StatsBuilderRowLayoutEnum.FOUR_FOUR_FOUR, name: '33% / 33% / 33%', percentages: [33, 33, 33], grid: ['csd-one-big', 'csd-one-big', 'csd-one-big'],
        },
      ],
      [
        {
          type: StatsBuilderRowLayoutEnum.THREE_THREE_THREE_THREE,
          name: t('statsEditor.addRowWidget.equalWidth'),
          percentages: [25, 25, 25, 25],
          grid: ['csd-one', 'csd-one', 'csd-one', 'csd-one'],
        },
      ],
    ];

    const columnsLayout: ComputedRef<any[]> = computed(() => availableColumnsLayouts[(chosenColumns.value - 1)] || []);

    const selectedLayout = computed(() => columnsLayout.value[layoutIndex.value] || columnsLayout.value[0]);

    const setDefaultColumns = () => {
      const defaultOption = availableColumns.value.find((option) => !option.disabled) || availableColumns.value[0];
      if (defaultOption) {
        chosenColumns.value = defaultOption.value;
      }
    };

    const initializeState = () => {
      isInitializing.value = true;

      if (isEditMode.value && props.rowData) {
        const initialColumns = props.rowData.children?.length ?? 1;
        chosenColumns.value = initialColumns;
        const layoutGroup = availableColumnsLayouts[(initialColumns - 1)] || [];
        const currentLayoutIndex = layoutGroup.findIndex((layout) => layout.type === props.rowData?.layout);
        layoutIndex.value = currentLayoutIndex >= 0 ? currentLayoutIndex : 0;
      } else {
        setDefaultColumns();
        layoutIndex.value = 0;
      }

      isInitializing.value = false;
    };

    const resetState = () => {
      isInitializing.value = true;
      if (isEditMode.value && props.rowData) {
        chosenColumns.value = props.rowData.children?.length ?? 1;
      } else {
        setDefaultColumns();
      }
      layoutIndex.value = 0;
      isInitializing.value = false;
    };

    watch(() => props.visible, (visible) => {
      if (visible) {
        initializeState();
      } else {
        resetState();
      }
    });

    watch(() => props.rowData, () => {
      if (props.visible && isEditMode.value) {
        initializeState();
      }
    });

    watch(chosenColumns, () => {
      if (!isInitializing.value) {
        layoutIndex.value = 0;
      }
    });

    const handleValidation = () => {
      if (!selectedLayout.value) {
        handleClose();
        return;
      }

      if (isEditMode.value && props.rowData) {
        store.dispatch('statsEditor/updateRowLayout', {
          rowId: props.rowData.id,
          layout: selectedLayout.value.type,
        });
      } else {
        const columnsLayoutArray = selectedLayout.value.type.split('-');
        const children: StatsBuilderColumnConfig[] = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const item of columnsLayoutArray) {
          const columnConfig: StatsBuilderColumnConfig = {
            id: `column_${uniqueIdS6Generator()}`,
            type: 'StatsWidgetColumn',
            layout: StatsBuilderColumnLayoutEnum.TWELVE,
            widgets: [],
          };

          switch (item) {
            case '3':
              columnConfig.layout = StatsBuilderColumnLayoutEnum.THREE;
              break;
            case '4':
              columnConfig.layout = StatsBuilderColumnLayoutEnum.FOUR;
              break;
            case '6':
              columnConfig.layout = StatsBuilderColumnLayoutEnum.SIX;
              break;
            case '9':
              columnConfig.layout = StatsBuilderColumnLayoutEnum.NINE;
              break;
            case '12':
              columnConfig.layout = StatsBuilderColumnLayoutEnum.TWELVE;
              break;
            default:
              columnConfig.layout = StatsBuilderColumnLayoutEnum.TWELVE;
          }
          children.push(columnConfig);
        }

        store.dispatch('statsEditor/addRow', {
          rowData: {
            id: `row_${uniqueIdS6Generator()}`,
            type: 'StatsWidgetRow',
            layout: selectedLayout.value.type,
            children,
          },
        });
      }

      handleClose();
    };

    const handleClose = () => {
      dialogVisible.value = false;
    };

    const headerLabel = computed(() => (isEditMode.value
      ? t('statsEditor.editRowWidget.header')
      : t('statsEditor.addRowWidget.header')));

    return {
      t,
      dialogVisible,
      headerLabel,
      availableColumns,
      chosenColumns,
      columnsLayout,
      layoutIndex,
      isEditMode,
      handleValidation,
      handleClose,
    };
  },
});
